import React, { useState, useEffect,useRef   } from 'react';
import { BarChart3, Calendar, Users, Check, Plus, X, Clock, TrendingUp, Smile } from 'lucide-react';
import { CugMojiPicker } from '../static/CugMoji';
import { useTheme } from '../arrangement/ThemeContext';
// Thread Poll Component for displaying polls in threads
export const ThreadPollComponent = ({ poll, threadId, onVote, currentUserId, colors }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isVoting, setIsVoting] = useState(false);
  const [showResults, setShowResults] = useState(poll.has_voted);

  const handleOptionSelect = (optionId) => {
    if (poll.has_voted || !poll.is_active) return;
    
    if (poll.is_multiple_choice) {
      setSelectedOptions(prev => 
        prev.includes(optionId) 
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const handleVote = async () => {
    if (selectedOptions.length === 0 || isVoting) return;
    
    setIsVoting(true);
    try {
      await onVote(threadId, selectedOptions);
      setShowResults(true);
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setIsVoting(false);
    }
  };
  const isBrandImage = (val) => typeof val === 'string' && val.startsWith('https');

  const renderEmoji = (val, size = 24) => {
    if (!val) return null;
    return isBrandImage(val)
      ? <img src={val} alt="" style={{ width: size, height: size, objectFit: 'contain' }} draggable={false} />
      : <span style={{ fontSize: size }}>{val}</span>;
  };

  const isExpired = poll.ends_at && new Date(poll.ends_at) < new Date();
  const canVote = poll.is_active && !poll.has_voted && !isExpired;

  return (
    <div style={{
      marginTop: '20px',
      padding: '24px',
      background: 'linear-gradient(135deg, #1e3a3a 0%, #0f2027 100%)',
      borderRadius: '20px',
      border: '2px solid #2d4a4a',
      boxShadow: '0 8px 25px rgba(0, 212, 170, 0.15)'
    }}>
      {/* Poll Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #00d4aa, #10b981)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 212, 170, 0.3)'
          }}>
            <BarChart3 size={20} style={{ color: 'white' }} />
          </div>
          <div>
            <h4 style={{
              margin: 0,
              fontWeight: '700',
              color: '#e5e5e5',
              fontSize: '18px'
            }}>
              {poll.question}
            </h4>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '4px'
            }}>
              <span style={{
                fontSize: '14px',
                color: '#00d4aa',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Users size={12} />
                {poll.total_votes} votes
              </span>
              {poll.ends_at && (
                <span style={{
                  fontSize: '14px',
                  color: isExpired ? '#ef4444' : '#fbbf24',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Clock size={12} />
                  {isExpired ? 'Ended' : `Ends ${new Date(poll.ends_at).toLocaleDateString()}`}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Poll Status Badges */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {poll.is_multiple_choice && (
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              Multiple Choice
            </div>
          )}
          {poll.has_voted && (
            <div style={{
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Check size={12} />
              Voted
            </div>
          )}
          {isExpired && (
            <div style={{
              background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              Closed
            </div>
          )}
        </div>
      </div>

      {/* Poll Description */}
      {poll.description && (
        <p style={{
          color: '#b3b3b3',
          marginBottom: '20px',
          fontSize: '15px',
          lineHeight: '1.5'
        }}>
          {poll.description}
        </p>
      )}
      
      {/* Poll Options */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: canVote ? '20px' : '0'
      }}>
        {poll.options.map((option) => (
          <div
            key={option.id}
            onClick={() => handleOptionSelect(option.id)}
            style={{
              position: 'relative',
              padding: '18px',
              borderRadius: '16px',
              border: selectedOptions.includes(option.id) 
                ? '2px solid #00d4aa' 
                : showResults 
                  ? '2px solid #2d4a4a'
                  : '2px solid #444',
              backgroundColor: '#2a2a2a',
              cursor: canVote ? 'pointer' : 'default',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: selectedOptions.includes(option.id) ? 'scale(1.02)' : 'scale(1)',
              boxShadow: selectedOptions.includes(option.id) 
                ? '0 8px 25px rgba(0, 212, 170, 0.3)' 
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (canVote) {
                e.currentTarget.style.borderColor = '#00d4aa';
                e.currentTarget.style.transform = 'scale(1.01)';
              }
            }}
            onMouseLeave={(e) => {
              if (canVote && !selectedOptions.includes(option.id)) {
                e.currentTarget.style.borderColor = '#444';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            {/* Results Background Bar */}
            {showResults && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  background: 'linear-gradient(90deg, rgba(0, 212, 170, 0.15) 0%, rgba(0, 212, 170, 0.05) 100%)',
                  borderRadius: '14px',
                  transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: `${option.percentage}%`
                }}
              />
            )}
            
            {/* Option Content */}
            <div style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {/* Selection Indicator */}
                {canVote && (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: poll.is_multiple_choice ? '4px' : '50%',
                    border: '2px solid',
                    borderColor: selectedOptions.includes(option.id) ? '#00d4aa' : '#666',
                    background: selectedOptions.includes(option.id) ? '#00d4aa' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    {selectedOptions.includes(option.id) && (
                      <Check size={12} style={{ color: 'white' }} />
                    )}
                  </div>
                )}
                
                {option.emoji && (
                  <span style={{ fontSize: '24px' }}>{option.emoji}</span>
                )}
                <span style={{
                  fontWeight: '600',
                  color: '#e5e5e5',
                  fontSize: '16px'
                }}>
                  {option.text}
                </span>
              </div>
              
              {/* Results Display */}
              {showResults && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{
                    fontWeight: '600',
                    color: '#00d4aa',
                    fontSize: '15px'
                  }}>
                    {option.votes} votes
                  </span>
                  <div style={{
                    backgroundColor: '#00d4aa',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    minWidth: '45px',
                    textAlign: 'center'
                  }}>
                    {option.percentage}%
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Vote Button */}
      {canVote && (
        <button
          onClick={handleVote}
          disabled={selectedOptions.length === 0 || isVoting}
          style={{
            width: '100%',
            padding: '16px',
            background: selectedOptions.length > 0 
              ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
              : '#666',
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            fontSize: '16px',
            fontWeight: '700',
            cursor: selectedOptions.length > 0 ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isVoting ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Submitting Vote...
            </>
          ) : (
            <>
              <Check size={18} />
              Vote {poll.is_multiple_choice && selectedOptions.length > 1 ? `(${selectedOptions.length})` : ''}
            </>
          )}
        </button>
      )}
    </div>
  );
};

// Poll Creator Component for the Create Thread Modal
export const PollCreator = ({ pollData, setPollData, isVisible, colors }) => {
  const [newOption, setNewOption] = useState({ text: '', emoji: '' });
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(null);
   const emojiButtonRefs = useRef({});
  const addOption = () => {
    if (newOption.text.trim() && pollData.options.length < 10) {
      setPollData(prev => ({
        ...prev,
        options: [...prev.options, { ...newOption, id: Date.now() }]
      }));
      setNewOption({ text: '', emoji: '' });
    }
  };

  const isBrandImage = (val) => typeof val === 'string' && val.startsWith('https1');

  const renderEmoji = (val, size = 24) => {
    if (!val) return null;
    return isBrandImage(val)
      ? <img src={val} alt="" style={{ width: size, height: size, objectFit: 'contain' }} draggable={false} />
      : <span style={{ fontSize: size }}>{val}</span>;
  };

  const removeOption = (index) => {
    setPollData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, field, value) => {
    setPollData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const handleEmojiSelect = (emoji) => {
    if (emojiPickerOpen === 'new') {
      setNewOption(prev => ({ ...prev, emoji }));
    } else if (typeof emojiPickerOpen === 'number') {
      updateOption(emojiPickerOpen, 'emoji', emoji);
    }
    setEmojiPickerOpen(null);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      marginTop: '20px',
      padding: '24px',
      background: 'linear-gradient(135deg, #1e3a3a 0%, #0f2027 100%)',
      borderRadius: '16px',
      border: '2px solid #2d4a4a'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <BarChart3 size={24} style={{ color: '#00d4aa' }} />
        <h3 style={{
          margin: 0,
          color: '#e5e5e5',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Poll Settings
        </h3>
      </div>

      {/* Poll Question */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          color: '#e5e5e5',
          fontWeight: '500'
        }}>
          Poll Question *
        </label>
        <input
          type="text"
          value={pollData.question}
          onChange={(e) => setPollData(prev => ({ ...prev, question: e.target.value }))}
          placeholder="What would you like to ask?"
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '2px solid #444',
            borderRadius: '12px',
            fontSize: '16px',
            background: colors.bgCard,
            color: '#e5e5e5',
            outline: 'none',
            transition: 'border-color 0.3s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#00d4aa'}
          onBlur={(e) => e.target.style.borderColor = '#444'}
        />
      </div>



      {/* Poll Options */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '12px',
          color: '#e5e5e5',
          fontWeight: '500'
        }}>
          Poll Options *
        </label>
        
        {/* Existing Options */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: '12px'
        }}>
         {pollData.options.map((option, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: colors.bgCard,
              borderRadius: '8px',
              border: '1px solid #444'
            }}>
              <button
                ref={el => emojiButtonRefs.current[index] = el}
                type="button"
                onClick={() => setEmojiPickerOpen(emojiPickerOpen === index ? null : index)}
                style={{
                  width: '50px',
                  height: '36px',
                  padding: '8px',
                  border: emojiPickerOpen === index ? '2px solid #00d4aa' : '1px solid #555',
                  borderRadius: '6px',
                  fontSize: '16px',
                  textAlign: 'center',
                  background: '#333',
                  color: '#e5e5e5',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Click to choose emoji"
              >
                {option.emoji ? renderEmoji(option.emoji, 22) : <Smile size={16} style={{ color: '#888' }} />}
              </button>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(index, 'text', e.target.value)}
                placeholder={`Option ${index + 1}`}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #555',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#333',
                  color: '#e5e5e5',
                  outline: 'none'
                }}
              />
              <button
                onClick={() => removeOption(index)}
                style={{
                  padding: '8px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Add New Option */}
        {pollData.options.length < 10 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: colors.bgTertiary,
            borderRadius: '8px',
            border: '2px dashed #555'
          }}>
            <button
              ref={el => emojiButtonRefs.current['new'] = el}
              type="button"
              onClick={() => setEmojiPickerOpen(emojiPickerOpen === 'new' ? null : 'new')}
              style={{
                width: '50px',
                height: '36px',
                padding: '8px',
                border: emojiPickerOpen === 'new' ? '2px solid #00d4aa' : '1px solid #555',
                borderRadius: '6px',
                fontSize: '16px',
                textAlign: 'center',
                background: '#333',
                color: '#e5e5e5',
                outline: 'none',
                cursor: 'pointer',
                transition: 'border-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Click to choose emoji"
            >
               {newOption.emoji ? renderEmoji(newOption.emoji, 22) : <Smile size={16} style={{ color: '#888' }} />}
            </button>
            <input
              type="text"
              value={newOption.text}
              onChange={(e) => setNewOption(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Add new option..."
              onKeyPress={(e) => e.key === 'Enter' && addOption()}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #555',
                borderRadius: '6px',
                fontSize: '14px',
                background: '#333',
                color: '#e5e5e5',
                outline: 'none'
              }}
            />
            <button
              onClick={addOption}
              disabled={!newOption.text.trim()}
              style={{
                padding: '8px',
                background: newOption.text.trim() ? '#00d4aa' : '#666',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: newOption.text.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Poll Settings */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '16px'
      }}>
        {/* Multiple Choice Toggle */}
        <div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={pollData.is_multiple_choice}
              onChange={(e) => setPollData(prev => ({ ...prev, is_multiple_choice: e.target.checked }))}
              style={{
                width: '18px',
                height: '18px',
                accentColor: '#00d4aa'
              }}
            />
            <span style={{ color: '#e5e5e5', fontWeight: '500' }}>
              Allow multiple choices
            </span>
          </label>
        </div>

        {/* End Date */}
        <div>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            color: '#e5e5e5',
            fontWeight: '500'
          }}>
            End Date (Optional)
          </label>
          <input
            type="datetime-local"
            value={pollData.ends_at}
            onChange={(e) => setPollData(prev => ({ ...prev, ends_at: e.target.value }))}
            min={new Date().toISOString().slice(0, 16)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #555',
              borderRadius: '6px',
              fontSize: '14px',
              background: '#333',
              color: '#e5e5e5',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Validation Message */}
      {pollData.options.length < 2 && (
        <div style={{
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#ef4444',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⚠️ Polls need at least 2 options
        </div>
      )}
      <CugMojiPicker
        isOpen={emojiPickerOpen !== null}
        onSelect={handleEmojiSelect}
        onClose={() => setEmojiPickerOpen(null)}
        targetRef={emojiButtonRefs.current[emojiPickerOpen]}
      />
    </div>
  );
};

export const EnhancedCreateThreadModal = ({ 
  isOpen, 
  onClose, 
  onCreateThread, 
  userSeshRooms 
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [seshRoomId, setSeshRoomId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSeshRoom, setSelectedSeshRoom] = useState(null);
  const [showSeshRooms, setShowSeshRooms] = useState(false);
  const { colors } = useTheme();
  // always-a-poll defaults
  const [pollData, setPollData] = useState({
    question: '',
    description: '',
    options: [
      { text: '', emoji: '👍' },
      { text: '', emoji: '👎' }
    ],
    is_multiple_choice: false,
    ends_at: ''
  });

  const handleSeshRoomSelection = (room) => {
    if (room === null) {
      setSelectedSeshRoom(null);
      setSeshRoomId(null);
    } else {
      setSelectedSeshRoom(room);
      setSeshRoomId(room.id);
    }
    setShowSeshRooms(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    // poll validation (always required now)
    if (!pollData.question.trim() && !title.trim()) {
      alert('Please enter a poll question or a title');
      return;
    }
    const validOptions = pollData.options.filter(opt => opt.text.trim());
    if (validOptions.length < 2) {
      alert('Polls need at least 2 options');
      return;
    }

    setIsSubmitting(true);
    try {
      const threadData = {
        sesh_room_id: seshRoomId,
        title: title.trim(),
        description: description.trim(),
        isSeshRoom: seshRoomId !== null,
        thread_type: 'poll',                       // <- always a poll
        poll_data: {
          ...pollData,
          question: pollData.question || title,    // fallback to title
          options: validOptions
        }
      };

      await onCreateThread(threadData);

      // reset
      setTitle('');
      setDescription('');
      setSelectedSeshRoom(null);
      setSeshRoomId(null);
      setPollData({
        question: '',
        description: '',
        options: [
          { text: '', emoji: '👍' },
          { text: '', emoji: '👎' }
        ],
        is_multiple_choice: false,
        ends_at: ''
      });
      onClose();
    } catch (error) {
      console.error('Failed to create thread:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    }}>
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '20px',
        padding: '32px',
        width: '100%', maxWidth: '700px',
        maxHeight: '90vh', overflowY: 'auto',
        border: '1px solid #333'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '24px'
        }}>
          <h2 style={{ margin: 0, color: '#e5e5e5', fontSize: '24px', fontWeight: '600' }}>
            📊 Create New Poll
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '8px' }}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* (Optional) Post Location UI placeholder kept if you use it elsewhere */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500' }}>
              Post Location
            </label>
            {/* your existing location selection UI goes here if needed */}
          </div>

          {/* Title */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500' }}>
              Poll Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's your poll about? 📊"
              style={{
                width: '100%',
                padding: '16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '16px',
                background: colors.bgCard,
                color: '#e5e5e5',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4aa'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500' }}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add context to your poll..."
              rows={4}
              style={{
                width: '100%',
                padding: '16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '16px',
                background: colors.bgCard,
                color: '#e5e5e5',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4aa'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
            />
          </div>

          {/* Poll Creator (always visible) */}
          <PollCreator 
            pollData={pollData}
            setPollData={setPollData}
            isVisible={true}
            colors={colors}
          />

          {/* Submit */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                border: '2px solid #333',
                borderRadius: '12px',
                background: 'transparent',
                color: '#888',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                !title.trim() ||
                isSubmitting ||
                pollData.options.filter(opt => opt.text.trim()).length < 2
              }
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '12px',
                background: (
                  title.trim() && pollData.options.filter(opt => opt.text.trim()).length >= 2
                ) ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' : '#333',
                color: 'white',
                cursor: (
                  title.trim() && pollData.options.filter(opt => opt.text.trim()).length >= 2
                ) ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {isSubmitting ? 'Creating...' : '📊 Create Poll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};